import {
  AfterViewChecked,
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild
} from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import {DateMomentParserFormatter} from './date-picker-moment-parser-formatter';
import {AbstractControl, ControlValueAccessor, NgControl, NgModel, ValidationErrors, Validators} from '@angular/forms';
import {isEqual} from 'lodash-es';

export function DateMomentParserFormatterFactory() {
  const formatter = new DateMomentParserFormatter();
  formatter.formatString = 'DD-MMM-YYYY';
  return formatter;
}

@Component({
  selector: 'ds-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {provide: NgbDateParserFormatter, useFactory: DateMomentParserFormatterFactory}
  ]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit, AfterViewChecked {
  @ViewChild('innerInput', {static: true}) innerInput: ElementRef;
  @ViewChild('dp', {static: true}) datepicker: NgbInputDatepicker;

  @Input() placeholder = 'dd-MMM-yyyy';
  @Input() iconName: string;
  @Input() minDate: NgbDateStruct;
  @Input() maxDate: NgbDateStruct;
  @Input() disableFrom: NgbDateStruct;
  @Input() fieldName = '';
  @Input() label = '';
  @Input() readOnlyLabel;
  /** Object that contains an error message and an Icon class for error */
  @Input() error = {message: '', iconClass: ''};
  /** When showErrorMessages is set to false, showError() will always return false */
  @Input() showErrorMessages = true;
  @Input() disableTo: NgbDateStruct;
  @Input() iconClass = 'ds-icon--close-outline';
  @Input() readOnlyCssClass = 'px-2';
  @Input() wrongFormatMessage: string;
  @Input() disabledDateMessage: string;
  @Input() lateDateMessage: string;
  @Input() earlyDateMessage: string;
  @Input() set disabled(disabled: boolean) {
    this.isDisabled = disabled;
  }

  @Output() focusEvent = new EventEmitter<boolean>();
  @Output() isReadOnlyEvent = new EventEmitter<boolean>();

  errorMessageID: string;
  formControl: AbstractControl;
  hasFocus = false;
  innerValue: NgbDate;
  inputID: string;
  isDisabled = false;
  isReadOnly = false;
  markDisabled = (date: NgbDate) => date.after(this.disableFrom) && date.before(this.disableTo);
  formattedMinDate: string;

  set readOnlyState(isReadOnly: boolean) {
    this.isReadOnly = isReadOnly;
    this.isReadOnlyEvent.emit(isReadOnly);
  }

  /**
   * This component needs a value accessor reference to validate the input with angular validators.
   * Value accessor is not set using the providers to avoid running into a circular import.
   *
   * Further information:
   * https://material.angular.io/guide/creating-a-custom-form-field-control
   * https://stackoverflow.com/questions/44201298
   * https://stackoverflow.com/questions/57186593
   *
   * Please note that for when you use ngModel max and min validators a custom validator should be implemented.
   *
   * @param ngControl used when FormControl is applied to this input
   * @param ngModel used when ngModel is applied to this input
   * @param calendar used when ngModel is applied to this input
   */
  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() @Self() public ngModel: NgModel,
    private calendar: NgbCalendar,
    private cdr: ChangeDetectorRef,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
    if (ngModel) {
      ngModel.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (!this.readOnlyLabel) {
      this.readOnlyLabel = this.label;
    }
  }

  ngAfterViewInit() {
    this.setFormControl(this.ngControl, this.ngModel);
  }

  ngAfterViewChecked(): void {
    this.setReadOnlyState();
    this.cdr.detectChanges();
  }

  setReadOnlyState(): void {
    if (this.minDate && this.maxDate && isEqual(this.minDate, this.maxDate)) {
      this.setDisabledState(true);
      this.readOnlyState = true;
      const formatter = new DateMomentParserFormatter();
      formatter.formatString = 'DD MMM YYYY';
      this.formattedMinDate = formatter.format(this.minDate);
    } else {
      this.readOnlyState = false;
    }
  }

  /**
   * Setting the value accessor directly (instead of using the providers)
   * to avoid running into a circular import.
   *
   * @param ngControl used when formcontrol is applied to this input
   * @param ngModel used when ngModel is applied to this input
   */
  setFormControl(ngControl: NgControl, ngModel: NgModel) {
    if (ngModel && ngModel.control) {
      this.formControl = ngModel.control;
    } else if (ngControl && ngControl.control) {
      this.formControl = ngControl.control;
    }
    this.mergeValidators();
  }

  mergeValidators() {
    if (this.formControl) {
      this.formControl.setValidators(
        Validators.compose([
          this.formControl.validator,
          this.datepicker.validate.bind(this.datepicker)
        ])
      );
    }
  }

  get errors(): ValidationErrors | null {
    return this.formControl ? this.formControl.errors : null;
  }

  /**
   * Decides if this input field is valid according to validation attributes.
   *
   * @returns false if the content of this input is valid otherwise true.
   */
  get invalid(): boolean {
    return this.formControl ? this.formControl.invalid : false;
  }

  /**
   * Decides if an error message should be shown for this input field.
   *
   * @returns true if an error message should be shown.
   */
  get showError(): boolean {
    if (this.showErrorMessages) {
      return this.invalid;
    }
    return false;
  }

  get value(): NgbDate {
    return this.innerValue;
  }

  set value(value: NgbDate) {
    if (value !== undefined && this.value !== value) {
      this.innerValue = value;
      this.onChange(value);
      this.onTouch(value);
    }
  }

  writeValue(value: NgbDate) {
    this.value = value;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  onChange: any = () => {
    // This is intentional
  };

  onTouch: any = () => {
    // This is intentional
  };

  /**
   * Generates an id for the label to connect it to the input
   * (Required for screen readers)
   *
   * @returns the id for the current input label
   */
  @Input()
  set id(id: string) {
    this.inputID = id + '__input';
    this.errorMessageID = id + '_error-message';
  }

  isToday(date: NgbDate): boolean {
    return this.calendar.getToday().equals(date);
  }

  open(): void {
    if (!this.isDisabled) {
      this.datepicker.open();
      this.setHasFocus(!this.hasFocus);
    }
  }

  handleKeyInput() {
    if (this.datepicker && !this.datepicker.isOpen()) {
      this.open();
    }
  }

  /**
   * Prevents the ExpressionChangedAfterItHasBeenCheckedError when an input field is shown in a modal.
   *
   * @param isFocused new value of the hasFocus parameter
   */
  setHasFocus(isFocused: boolean = true) {
    this.hasFocus = isFocused;
    this.focusEvent.emit(isFocused);
  }

  setFocusState(isFocused: boolean = true) {
    if (isFocused) {
      // makes the dom render before this method is executed.
      setTimeout(() => {
        this.innerInput.nativeElement.focus();
      }, 0);
    } else {
      this.innerInput.nativeElement.blur();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
